import React, { useState, useEffect } from 'react';
import Navigation from '../components/Navigation';
import { getPublicSiteSettings } from '../services/api';
import './APropos.css';

const DEFAULT_VALUES = [
  { icon: '🤝', title: 'Solidarité', description: 'Nous croyons en la force de l\'entraide et de la solidarité communautaire.' },
  { icon: '🔍', title: 'Transparence', description: 'Gestion transparente et responsable de nos ressources et activités.' },
  { icon: '🌍', title: 'Accessibilité', description: 'Nos services sont accessibles à tous, sans discrimination.' },
  { icon: '⭐', title: 'Qualité', description: 'Services de qualité supérieure avec des partenaires reconnus.' },
  { icon: '💡', title: 'Innovation', description: 'Nouvelles technologies pour améliorer nos services.' },
  { icon: '❤️', title: 'Empathie', description: 'Nous accompagnons nos membres avec compassion et respect.' }
];

const DEFAULT_TEAM = [
  { name: 'Nom du Membre 1', role: 'Directeur Général', description: 'Expérience de plus de 10 ans.', photo: '/team-member-1.jpg' },
  { name: 'Nom du Membre 2', role: 'Responsable Santé', description: 'Spécialiste en santé publique.', photo: '/team-member-2.jpg' },
  { name: 'Nom du Membre 3', role: 'Responsable Juridique', description: 'Avocat spécialisé en droit social.', photo: '/team-member-3.jpg' },
  { name: 'Nom du Membre 4', role: 'Coordonnateur Social', description: 'Expert en développement communautaire.', photo: '/team-member-4.jpg' },
  { name: 'Nom du Membre 5', role: 'Administrateur', description: 'Gestionnaire expérimenté.', photo: '/team-member-5.jpg' },
  { name: 'Nom du Membre 6', role: 'Responsable Communication', description: 'Spécialiste en communication.', photo: '/team-member-6.jpg' }
];

const APropos = () => {
  const [siteSettings, setSiteSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPublicSiteSettings()
      .then((res) => res?.data && typeof res.data === 'object' && setSiteSettings(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const title = siteSettings?.apropos_title ?? 'À Propos de Nous';
  const subtitle = siteSettings?.apropos_subtitle ?? 'Découvrez MUTU NDE ESPOIR ASBL';
  const siteName = siteSettings?.site_name ?? 'MUTU NDE ESPOIR ASBL';
  const siteLogo = siteSettings?.site_logo ?? '/logo.png';
  const values = Array.isArray(siteSettings?.apropos_values) && siteSettings.apropos_values.length > 0
    ? siteSettings.apropos_values
    : DEFAULT_VALUES;
  const team = Array.isArray(siteSettings?.apropos_team) && siteSettings.apropos_team.length > 0
    ? siteSettings.apropos_team
    : DEFAULT_TEAM;

  if (loading) {
    return (
      <div className="apropos-page">
        <Navigation siteName={siteName} siteLogo={siteLogo} />
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div className="spinner" style={{ margin: '0 auto 20px' }}></div>
          <p>Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="apropos-page">
      <Navigation siteName={siteName} siteLogo={siteLogo} />
      
      <div className="page-header">
        <div className="container">
          <h1 className="page-title">{title}</h1>
          <p className="page-subtitle">{subtitle}</p>
        </div>
      </div>

      <div className="container">
        <section className="mission-section">
          <h2>{siteSettings?.apropos_mission_title ?? 'Notre Mission'}</h2>
          <div className="mission-content">
            <p>{siteSettings?.apropos_mission_p1 ?? `${siteName} a pour mission de fournir un accès équitable aux services essentiels de santé, d'assistance juridique et de soutien social à tous les membres de notre communauté, sans discrimination.`}</p>
            <p>{siteSettings?.apropos_mission_p2 ?? "Nous croyons que chaque personne mérite d'avoir accès à des soins de qualité, à une protection juridique et à un soutien social, indépendamment de sa situation économique ou sociale."}</p>
          </div>
        </section>

        <section className="vision-section">
          <h2>{siteSettings?.apropos_vision_title ?? 'Notre Vision'}</h2>
          <div className="vision-content">
            <p>{siteSettings?.apropos_vision_p1 ?? "Nous aspirons à devenir la référence en matière de mutuelle de solidarité en République Démocratique du Congo, en offrant des services innovants, accessibles et de qualité qui améliorent significativement la qualité de vie de nos membres."}</p>
            <p>{siteSettings?.apropos_vision_p2 ?? "Notre vision est de créer une communauté solidaire où chaque membre se sent soutenu, protégé et valorisé, contribuant ainsi au développement social et économique de notre région."}</p>
          </div>
        </section>

        <section className="values-section">
          <h2>{siteSettings?.apropos_values_title ?? 'Nos Valeurs Fondamentales'}</h2>
          <div className="values-grid">
            {values.filter(v => v && (v.title || v.description)).map((v, i) => (
              <div key={i} className="value-card">
                <div className="value-icon">{v.icon || '•'}</div>
                <h3>{v.title || ''}</h3>
                <p>{v.description || ''}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="team-section">
          <h2>{siteSettings?.apropos_team_title ?? 'Notre Équipe'}</h2>
          <div className="team-content">
            <p style={{ textAlign: 'center', marginBottom: '40px', fontSize: '1.1rem', color: '#666' }}>
              {siteSettings?.apropos_team_intro ?? `${siteName} est dirigée par une équipe dévouée de professionnels passionnés par la solidarité et l'amélioration des conditions de vie de la communauté.`}
            </p>
            <div className="team-grid">
              {team.filter(t => t && (t.name || t.role || t.description)).map((member, i) => (
                <div key={i} className="team-member">
                  <div className="team-member-photo">
                    <img src={member.photo || '/logo.png'} alt={member.name || 'Membre'} onError={(e) => { e.target.src = '/logo.png'; }} />
                  </div>
                  <div className="team-member-info">
                    <h3>{member.name || ''}</h3>
                    <p className="team-member-role">{member.role || ''}</p>
                    <p className="team-member-description">{member.description || ''}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="location-section">
          <h2>{siteSettings?.apropos_location_title ?? 'Notre Localisation'}</h2>
          <div className="location-content">
            <div className="location-info">
              <div className="location-icon">📍</div>
              <div>
                <h3>Kisangani, RDC</h3>
                <p>
                  {siteSettings?.apropos_location_text ?? `${siteName} est basée à Kisangani, en République Démocratique du Congo. Nous servons principalement la communauté locale tout en étant ouverts à l'expansion de nos services.`}
                </p>
              </div>
            </div>
            <div className="location-image">
              <img src="/rdc.png" alt="RDC" className="rdc-map" />
            </div>
          </div>
        </section>
      </div>
      
      <footer className="presentation-footer">
        <div className="container">
          <div className="footer-bottom">
            <p>&copy; {new Date().getFullYear()} {siteName}. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default APropos;
